import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDoctorSettings, updateDoctorSettings } from '../../redux/doctorSlice';
import { restrictnavigate, tryingnavigate, clearPendingNavigation } from '../../redux/blockingSlice';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import CommonLoader from '../CommonLoader';

// Early-open restriction duration is stored on the server as total minutes;
// decompose using 30-day months so the UI can show months/days/hours/minutes.
const MIN_PER_HOUR = 60;
const MIN_PER_DAY = 24 * MIN_PER_HOUR;
const MIN_PER_MONTH = 30 * MIN_PER_DAY;

const decomposeDuration = (totalMinutes) => {
    const safe = Math.max(0, parseInt(totalMinutes, 10) || 0);
    const months = Math.floor(safe / MIN_PER_MONTH);
    let rem = safe % MIN_PER_MONTH;
    const days = Math.floor(rem / MIN_PER_DAY);
    rem = rem % MIN_PER_DAY;
    const hours = Math.floor(rem / MIN_PER_HOUR);
    const minutes = rem % MIN_PER_HOUR;
    return { months, days, hours, minutes };
};

// How far into the past a doctor/admin may book/reschedule when the restriction
// is turned OFF. -1 = unrestricted (any past date).
const PAST_WINDOW_OPTIONS = [
    { value: 10, label: '10 days' },
    { value: 30, label: '1 month' },
    { value: 90, label: '3 months' },
    { value: -1, label: 'Unrestricted' },
];

// 4-stop progress slider (replicated from the admin Settings page so it can be
// reused here without touching that file).
const SegmentedSlider = ({ options, value, onChange, disabled, testId }) => {
    const found = options.findIndex((o) => o.value === value);
    const idx = found === -1 ? 0 : found;
    const lastIdx = options.length - 1;
    const pct = lastIdx === 0 ? 0 : (idx / lastIdx) * 100;

    return (
        <div className="w-full select-none" data-testid={testId}>
            <div className="relative h-6">
                <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gray-200" />
                <div
                    className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition-all duration-200"
                    style={{ width: `${pct}%` }}
                />
                {options.map((o, i) => {
                    const left = lastIdx === 0 ? 0 : (i / lastIdx) * 100;
                    const reached = i <= idx;
                    return (
                        <button
                            key={o.value}
                            type="button"
                            disabled={disabled}
                            onClick={() => onChange(o.value)}
                            data-testid={`${testId}-stop-${o.value}`}
                            aria-label={o.label}
                            className={`absolute top-1/2 z-10 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-white transition-colors ${reached ? 'border-[#4ABDBD]' : 'border-gray-300'} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            style={{ left: `${left}%` }}
                        />
                    );
                })}
                <div
                    className="pointer-events-none absolute top-1/2 z-20 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-md ring-2 ring-[#4ABDBD] transition-all duration-200"
                    style={{ left: `${pct}%` }}
                />
                <input
                    type="range"
                    min={0}
                    max={lastIdx}
                    step={1}
                    value={idx}
                    disabled={disabled}
                    onChange={(e) => onChange(options[Number(e.target.value)].value)}
                    data-testid={`${testId}-range`}
                    className={`absolute inset-0 z-30 h-full w-full opacity-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                />
            </div>
            <div className="relative mt-3 h-5">
                {options.map((o, i) => {
                    const left = lastIdx === 0 ? 0 : (i / lastIdx) * 100;
                    const active = i === idx;
                    const transform = i === 0 ? 'translateX(0)' : i === lastIdx ? 'translateX(-100%)' : 'translateX(-50%)';
                    return (
                        <span
                            key={o.value}
                            className={`absolute top-0 whitespace-nowrap text-xs ${active ? 'font-semibold text-[#3AA8A8]' : 'text-gray-400'}`}
                            style={{ left: `${left}%`, transform }}
                        >
                            {o.label}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

const DoctorSettings = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();


    const { doctorSettings, loading, selectedDoctor } = useSelector((state) => state.doctor);
    const { trynavigate } = useSelector((state) => state.blocking);
    const doctorId = selectedDoctor?.doctorId;

    const [calendarStatus, setCalendarStatus] = useState(false);
    const [months, setMonths] = useState(0);
    const [days, setDays] = useState(0);
    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(15);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [businessHour, setBusinessHour] = useState(false);
    const [leaveRestriction, setLeaveRestriction] = useState(false);
    const [breakRestriction, setBreakRestriction] = useState(false);
    const [earlyOpenRestriction, setEarlyOpenRestriction] = useState(true);
    // Past-date restriction: ON = doctors/admins can't act on past dates (default,
    // current behavior). OFF = allowed back as far as pastDateWindowDays.
    const [pastDateRestriction, setPastDateRestriction] = useState(true);
    const [pastDateWindowDays, setPastDateWindowDays] = useState(10);
    const originalSettingsRef = useRef(null);
    const isNavigatingRef = useRef(false);

    useEffect(() => {
        if (doctorSettings) {
            const totalMinutes = doctorSettings.duration || 15;
            const parts = decomposeDuration(totalMinutes);
            // Enforce 15-min floor only when months/days/hours are all zero, so
            // a "less than 15 minutes" total can't be configured by accident.
            const safeMinutes = parts.months === 0 && parts.days === 0 && parts.hours === 0 && parts.minutes < 15
                ? 15
                : parts.minutes;

            setCalendarStatus(doctorSettings.status);
            setMonths(parts.months);
            setDays(parts.days);
            setHours(parts.hours);
            setMinutes(safeMinutes);
            setBusinessHour(doctorSettings.businessHour || false);
            setLeaveRestriction(doctorSettings.leaveRestriction || false);
            setBreakRestriction(doctorSettings.breakRestriction || false);
            setEarlyOpenRestriction(doctorSettings.earlyOpenRestriction !== false);
            setPastDateRestriction(doctorSettings.pastDateRestriction !== false);
            setPastDateWindowDays(doctorSettings.pastDateWindowDays ?? 10);

            originalSettingsRef.current = {
                calendarStatus: doctorSettings.status,
                months: parts.months,
                days: parts.days,
                hours: parts.hours,
                minutes: safeMinutes,
                businessHour: doctorSettings.businessHour || false,
                leaveRestriction: doctorSettings.leaveRestriction || false,
                breakRestriction: doctorSettings.breakRestriction || false,
                earlyOpenRestriction: doctorSettings.earlyOpenRestriction !== false,
                pastDateRestriction: doctorSettings.pastDateRestriction !== false,
                pastDateWindowDays: doctorSettings.pastDateWindowDays ?? 10,
            };
        }
    }, [doctorSettings]);

    // Detect unsaved changes
    const hasChanges = () => {
        const orig = originalSettingsRef.current;
        if (!orig) return false;
        return (
            calendarStatus !== orig.calendarStatus ||
            parseInt(months) !== orig.months ||
            parseInt(days) !== orig.days ||
            parseInt(hours) !== orig.hours ||
            parseInt(minutes) !== orig.minutes ||
            businessHour !== orig.businessHour ||
            leaveRestriction !== orig.leaveRestriction ||
            breakRestriction !== orig.breakRestriction ||
            earlyOpenRestriction !== orig.earlyOpenRestriction ||
            pastDateRestriction !== orig.pastDateRestriction ||
            parseInt(pastDateWindowDays) !== orig.pastDateWindowDays
        );
    };

    // Block navigation when there are unsaved changes
    useEffect(() => {
        if (isNavigatingRef.current) return;
        dispatch(restrictnavigate(hasChanges()));
    }, [calendarStatus, months, days, hours, minutes, businessHour, leaveRestriction, breakRestriction, earlyOpenRestriction, pastDateRestriction, pastDateWindowDays, dispatch]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isNavigatingRef.current = false;
            dispatch(restrictnavigate(false));
        };
    }, [dispatch]);

    // Handle navigation attempt with unsaved changes
    useEffect(() => {
        if (trynavigate && hasChanges() && !isNavigatingRef.current) {
            isNavigatingRef.current = true;
            Swal.fire({
                title: t('doctorsettings.unsaved_changes_title', 'Unsaved Changes'),
                text: t('doctorsettings.unsaved_changes_text', 'You have unsaved changes. Do you want to discard them?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('doctorsettings.discard', 'Discard'),
                cancelButtonText: t('doctorsettings.stay', 'Stay'),
                confirmButtonColor: '#d33',
                cancelButtonColor: '#9ca3af',
                allowOutsideClick: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    // Discard changes — leave pendingNavigation in place so the
                    // sidebar / DoctorSettingsPage consumer can auto-redirect to
                    // the destination the user originally clicked.
                    const orig = originalSettingsRef.current;
                    if (orig) {
                        setCalendarStatus(orig.calendarStatus);
                        setMonths(orig.months);
                        setDays(orig.days);
                        setHours(orig.hours);
                        setMinutes(orig.minutes);
                        setBusinessHour(orig.businessHour);
                        setLeaveRestriction(orig.leaveRestriction);
                        setBreakRestriction(orig.breakRestriction);
                        setEarlyOpenRestriction(orig.earlyOpenRestriction);
                        setPastDateRestriction(orig.pastDateRestriction);
                        setPastDateWindowDays(orig.pastDateWindowDays);
                    }
                    dispatch(restrictnavigate(false));
                    dispatch(tryingnavigate(false));
                } else {
                    // Stay — drop the pending destination so a later (independent)
                    // navigation attempt doesn't auto-fire stale state.
                    isNavigatingRef.current = false;
                    dispatch(tryingnavigate(false));
                    dispatch(clearPendingNavigation());
                }
            });
        } else if (trynavigate) {
            // No unsaved changes (e.g. right after a successful save) — allow it.
            // Also clear the dirty guard so the sidebar's pending-navigation effect
            // (which only fires when resnavigate === false) can consume the
            // destination; otherwise a stale resnavigate=true swallows every
            // sidebar click until a browser refresh.
            if (!hasChanges()) {
                dispatch(restrictnavigate(false));
            }
            dispatch(tryingnavigate(false));
        }
    }, [trynavigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const totalDuration =
            (parseInt(months) * MIN_PER_MONTH) +
            (parseInt(days) * MIN_PER_DAY) +
            (parseInt(hours) * MIN_PER_HOUR) +
            parseInt(minutes);

        if (earlyOpenRestriction && totalDuration < 15) {
            toast.warn(t('doctorsettings.min_duration_warning', 'Minimum duration must be at least 15 minutes.'));
            return;
        }

        const result = await Swal.fire({
            title: t('doctorsettings.confirm_update_title'),
            text: t('doctorsettings.confirm_update_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('doctorsettings.confirm_update_button'),
            cancelButtonText: t('doctorsettings.cancel'),
        });

        if (result.isConfirmed) {
            setIsSubmitting(true);
            try {
                await dispatch(
                    updateDoctorSettings({
                        doctorId: doctorId,
                        status: calendarStatus,
                        duration: earlyOpenRestriction ? totalDuration : 0,
                        businessHour: businessHour,
                        leaveRestriction: leaveRestriction,
                        breakRestriction: breakRestriction,
                        earlyOpenRestriction: earlyOpenRestriction,
                        pastDateRestriction: pastDateRestriction,
                        pastDateWindowDays: parseInt(pastDateWindowDays),
                    })
                ).unwrap();

                await dispatch(getDoctorSettings(doctorId));

                // Update original ref so hasChanges() returns false
                originalSettingsRef.current = {
                    calendarStatus,
                    months: parseInt(months),
                    days: parseInt(days),
                    hours: parseInt(hours),
                    minutes: parseInt(minutes),
                    businessHour,
                    leaveRestriction,
                    breakRestriction,
                    earlyOpenRestriction,
                    pastDateRestriction,
                    pastDateWindowDays: parseInt(pastDateWindowDays),
                };

                // The restrictnavigate effect only re-runs when a field value
                // changes; a save mutates originalSettingsRef instead, so it would
                // leave resnavigate stuck true and silently block every sidebar
                // click until a refresh. Clear the guard explicitly here.
                dispatch(restrictnavigate(false));

                toast.success(t('doctorsettings.update_success_text'));
            } catch (error) {
                toast.warn(t('doctorsettings.update_error_text'));
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    if (loading && !doctorSettings) {
        return (
            <div className="flex flex-col items-center justify-center py-8 sm:py-10 md:py-12 min-h-[50vh]" data-testid="doctor-settings-loading">
                <CommonLoader />
                <span className="text-sm sm:text-base md:text-lg text-[#4ABDBD] mt-3 sm:mt-4" data-testid="loading-text">
                    {t('doctorsettings.loading')}
                </span>
            </div>
        );
    }

    return (
        <div className=" mx-auto  mt-4 overflow-hidden" data-testid="doctor-settings-container">
            <form onSubmit={handleSubmit} data-testid="doctor-settings-form">
                {/* Calendar Settings Section */}
                <div className="mb-6 sm:mb-8 md:mb-10" data-testid="doctor-settings-calendar-section">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-3 sm:mb-4" data-testid="doctor-settings-calendar-event-view-heading">
                        {t('doctorsettings.calendar_event_view')}
                    </h3>
                    <div className="space-y-3 sm:space-y-4">
                        <label className="relative flex items-center gap-3 cursor-pointer group p-3 sm:p-4 rounded-xl border border-gray-200 hover:border-[#4ABDBD] transition-all duration-300 hover:shadow-sm" data-testid="doctor-settings-radio-booked-label">
                            <div className="flex-1">
                                <span className={`text-sm sm:text-base md:text-lg font-semibold transition-colors duration-300 ${
                                    calendarStatus === false
                                        ? 'text-[#4ABDBD]'
                                        : 'text-gray-700 group-hover:text-[#4ABDBD]'
                                }`} data-testid="doctor-settings-radio-booked-title">
                                    {t('doctorsettings.booked')}
                                </span>
                                <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2" data-testid="doctor-settings-radio-booked-description">
                                    {t('doctorsettings.booked_description')}
                                </p>
                            </div>
                            <div className="relative">
                                <input
                                    type="radio"
                                    name="calendarStatus"
                                    checked={calendarStatus === false}
                                    onChange={() => setCalendarStatus(false)}
                                    className="sr-only"
                                    data-testid="radio-booked"
                                />
                                <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                                    calendarStatus === false 
                                        ? 'border-[#4ABDBD] bg-[#4ABDBD]' 
                                        : 'border-gray-300 bg-white group-hover:border-[#4ABDBD]'
                                }`}>
                                    {calendarStatus === false && (
                                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-white rounded-full"></div>
                                    )}
                                </div>
                            </div>
                        </label>
                        
                        <label className="relative flex items-center gap-3 cursor-pointer group p-3 sm:p-4 rounded-xl border border-gray-200 hover:border-[#4ABDBD] transition-all duration-300 hover:shadow-sm" data-testid="doctor-settings-radio-booked-completed-label">
                            <div className="flex-1">
                                <span className={`text-sm sm:text-base md:text-lg font-semibold transition-colors duration-300 ${
                                    calendarStatus === true
                                        ? 'text-[#4ABDBD]'
                                        : 'text-gray-700 group-hover:text-[#4ABDBD]'
                                }`} data-testid="doctor-settings-radio-booked-completed-title">
                                    {t('doctorsettings.booked_completed')}
                                </span>
                                <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2" data-testid="doctor-settings-radio-booked-completed-description">
                                    {t('doctorsettings.booked_completed_description')}
                                </p>
                            </div>
                            <div className="relative">
                                <input
                                    type="radio"
                                    name="calendarStatus"
                                    checked={calendarStatus === true}
                                    onChange={() => setCalendarStatus(true)}
                                    className="sr-only"
                                    data-testid="radio-booked-completed"
                                />
                                <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                                    calendarStatus === true 
                                        ? 'border-[#4ABDBD] bg-[#4ABDBD]' 
                                        : 'border-gray-300 bg-white group-hover:border-[#4ABDBD]'
                                }`}>
                                    {calendarStatus === true && (
                                        <div className="w-2 h-2 sm:w-3 sm:h-3 bg-white rounded-full"></div>
                                    )}
                                </div>
                            </div>
                        </label>
                    </div>

                    {/* Calendar Slot Restriction Section */}
                    <div className="mt-6 sm:mt-8 border-t border-gray-200 pt-4 sm:pt-6" data-testid="doctor-settings-calendar-slot-restriction-section">
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-3 sm:mb-4" data-testid="doctor-settings-calendar-slot-restriction-heading">
                            {t('doctorSettings.calendar_slot_restriction')}
                        </h3>
                        <div className="space-y-3 sm:space-y-4">
                            {/* Non-Business Hour Restriction */}
                            <div
                                className="relative flex items-center justify-between cursor-pointer group p-3 sm:p-4 rounded-xl border border-gray-200 hover:border-[#4ABDBD] transition-all duration-300 hover:shadow-sm"
                                onClick={() => setBusinessHour(!businessHour)}
                                data-testid="doctor-settings-business-hour-row"
                            >
                                <div className="flex-1">
                                    <span className={`text-sm sm:text-base md:text-lg font-semibold transition-colors duration-300 ${
                                        businessHour === true
                                            ? 'text-[#4ABDBD]'
                                            : 'text-gray-700 group-hover:text-[#4ABDBD]'
                                    }`} data-testid="doctor-settings-business-hour-label">
                                        {t('doctorSettings.non_business_hour_restriction')}
                                    </span>
                                    <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2" data-testid="doctor-settings-business-hour-description">
                                        {businessHour === true
                                            ? t('doctorSettings.non_business_hour_restricted_desc', 'Non-business hours are restricted. Appointments cannot be handled outside business hours.')
                                            : t('doctorSettings.non_business_hour_open_desc', 'Non-business hours are not restricted. Appointments can be handled outside business hours.')
                                        }
                                    </p>
                                </div>
                                <div className="relative ml-4">
                                    <div className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 ${
                                        businessHour === true
                                            ? 'bg-[#4ABDBD]'
                                            : 'bg-gray-300'
                                    }`} data-testid="doctor-settings-business-hour-toggle">
                                        <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${
                                            businessHour === true
                                                ? 'translate-x-[24px] sm:translate-x-[24px]'
                                                : 'translate-x-1'
                                        }`}></span>
                                    </div>
                                </div>
                            </div>

                            {/* Leave Restriction */}
                            <div
                                className="relative flex items-center justify-between cursor-pointer group p-3 sm:p-4 rounded-xl border border-gray-200 hover:border-[#4ABDBD] transition-all duration-300 hover:shadow-sm"
                                onClick={() => setLeaveRestriction(!leaveRestriction)}
                                data-testid="doctor-settings-leave-restriction-row"
                            >
                                <div className="flex-1">
                                    <span className={`text-sm sm:text-base md:text-lg font-semibold transition-colors duration-300 ${
                                        leaveRestriction === true
                                            ? 'text-[#4ABDBD]'
                                            : 'text-gray-700 group-hover:text-[#4ABDBD]'
                                    }`} data-testid="doctor-settings-leave-restriction-label">
                                        {t('doctorSettings.leave_restriction')}
                                    </span>
                                    <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2" data-testid="doctor-settings-leave-restriction-description">
                                        {leaveRestriction === true
                                            ? t('doctorSettings.leave_restricted_desc', 'Leave days are restricted. Appointments cannot be handled during leave periods.')
                                            : t('doctorSettings.leave_open_desc', 'Leave days are not restricted. Appointments can be handled during leave periods.')
                                        }
                                    </p>
                                </div>
                                <div className="relative ml-4">
                                    <div className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 ${
                                        leaveRestriction === true
                                            ? 'bg-[#4ABDBD]'
                                            : 'bg-gray-300'
                                    }`} data-testid="doctor-settings-leave-restriction-toggle">
                                        <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${
                                            leaveRestriction === true
                                                ? 'translate-x-[24px] sm:translate-x-[24px]'
                                                : 'translate-x-1'
                                        }`}></span>
                                    </div>
                                </div>
                            </div>

                            {/* Break Restriction */}
                            <div
                                className="relative flex items-center justify-between cursor-pointer group p-3 sm:p-4 rounded-xl border border-gray-200 hover:border-[#4ABDBD] transition-all duration-300 hover:shadow-sm"
                                onClick={() => setBreakRestriction(!breakRestriction)}
                                data-testid="doctor-settings-break-restriction-row"
                            >
                                <div className="flex-1">
                                    <span className={`text-sm sm:text-base md:text-lg font-semibold transition-colors duration-300 ${
                                        breakRestriction === true
                                            ? 'text-[#4ABDBD]'
                                            : 'text-gray-700 group-hover:text-[#4ABDBD]'
                                    }`} data-testid="doctor-settings-break-restriction-label">
                                        {t('doctorSettings.break_restriction')}
                                    </span>
                                    <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2" data-testid="doctor-settings-break-restriction-description">
                                        {breakRestriction === true
                                            ? t('doctorSettings.break_restricted_desc', 'Break times are restricted. Appointments cannot be handled during break periods.')
                                            : t('doctorSettings.break_open_desc', 'Break times are not restricted. Appointments can be handled during break periods.')
                                        }
                                    </p>
                                </div>
                                <div className="relative ml-4">
                                    <div className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 ${
                                        breakRestriction === true
                                            ? 'bg-[#4ABDBD]'
                                            : 'bg-gray-300'
                                    }`} data-testid="doctor-settings-break-restriction-toggle">
                                        <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${
                                            breakRestriction === true
                                                ? 'translate-x-[24px] sm:translate-x-[24px]'
                                                : 'translate-x-1'
                                        }`}></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Past Date Restriction Section */}
                <div className="border-t border-gray-200 pt-4 sm:pt-6 mb-6 sm:mb-8 md:mb-10" data-testid="doctor-settings-past-date-section">
                    {/* Toggle Switch */}
                    <div
                        className="relative flex items-center justify-between cursor-pointer group p-3 sm:p-4 rounded-xl border border-gray-200 hover:border-[#4ABDBD] transition-all duration-300 hover:shadow-sm mb-4"
                        onClick={() => setPastDateRestriction(!pastDateRestriction)}
                        data-testid="doctor-settings-past-date-restriction-row"
                    >
                        <div className="flex-1">
                            <span className={`text-sm sm:text-base md:text-lg font-semibold transition-colors duration-300 ${
                                pastDateRestriction ? 'text-[#4ABDBD]' : 'text-gray-700 group-hover:text-[#4ABDBD]'
                            }`} data-testid="doctor-settings-past-date-restriction-label">
                                {t('doctorsettings.past_date_restriction_label', 'Restrict Past Date & Time')}
                            </span>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2" data-testid="doctor-settings-past-date-restriction-description">
                                {pastDateRestriction
                                    ? t('doctorsettings.past_date_enabled_desc', 'Doctors and admins cannot book or reschedule appointments on past dates and times.')
                                    : t('doctorsettings.past_date_disabled_desc', 'Doctors and admins can book or reschedule into the past, up to the window selected below.')
                                }
                            </p>
                        </div>
                        <div className="relative ml-4">
                            <div className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 ${
                                pastDateRestriction ? 'bg-[#4ABDBD]' : 'bg-gray-300'
                            }`} data-testid="doctor-settings-past-date-restriction-toggle">
                                <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${
                                    pastDateRestriction ? 'translate-x-6' : 'translate-x-1'
                                }`}></span>
                            </div>
                        </div>
                    </div>

                    {/* Past window slider — only shown when the restriction is OFF */}
                    {!pastDateRestriction && (
                        <div className="px-3 sm:px-4 pt-2 pb-2" data-testid="doctor-settings-past-window-block">
                            <label className="block text-gray-600 font-semibold text-xs sm:text-sm uppercase tracking-wide mb-3">
                                {t('doctorsettings.past_window_label', 'Allow booking into the past up to')}
                            </label>
                            <SegmentedSlider
                                options={PAST_WINDOW_OPTIONS}
                                value={pastDateWindowDays}
                                onChange={setPastDateWindowDays}
                                testId="doctor-settings-past-window-slider"
                            />
                        </div>
                    )}
                </div>

                {/* Appointment Control Duration Section */}
                <div className="border-t border-gray-200 pt-4 sm:pt-6 mb-6 sm:mb-8 md:mb-10" data-testid="doctor-settings-appointment-control-section">
                    {/* Toggle Switch */}
                    <div
                        className="relative flex items-center justify-between cursor-pointer group p-3 sm:p-4 rounded-xl border border-gray-200 hover:border-[#4ABDBD] transition-all duration-300 hover:shadow-sm mb-4"
                        onClick={() => setEarlyOpenRestriction(!earlyOpenRestriction)}
                        data-testid="doctor-settings-early-open-restriction-row"
                    >
                        <div className="flex-1">
                            <span className={`text-sm sm:text-base md:text-lg font-semibold transition-colors duration-300 ${
                                earlyOpenRestriction ? 'text-[#4ABDBD]' : 'text-gray-700 group-hover:text-[#4ABDBD]'
                            }`} data-testid="doctor-settings-early-open-restriction-label">
                                {t('doctorsettings.early_open_restriction_label', 'Early Open Restriction')}
                            </span>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2" data-testid="doctor-settings-early-open-restriction-description">
                                {earlyOpenRestriction
                                    ? t('doctorsettings.early_open_enabled_desc', 'Appointment open and connect meet buttons are restricted until the configured time before the appointment.')
                                    : t('doctorsettings.early_open_disabled_desc', 'No time restriction. Doctor can open appointment and connect meet at any time.')
                                }
                            </p>
                        </div>
                        <div className="relative ml-4">
                            <div className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 ${
                                earlyOpenRestriction ? 'bg-[#4ABDBD]' : 'bg-gray-300'
                            }`} data-testid="doctor-settings-early-open-restriction-toggle">
                                <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${
                                    earlyOpenRestriction ? 'translate-x-6' : 'translate-x-1'
                                }`}></span>
                            </div>
                        </div>
                    </div>

                    {/* Months / Days / Hours / Minutes - only shown when toggle is ON */}
                    {earlyOpenRestriction && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                            <div className="space-y-2">
                                <label className="block text-gray-600 font-semibold text-xs sm:text-sm uppercase tracking-wide" data-testid="months-label">
                                    {t('doctorsettings.months_label', 'Months')}
                                </label>
                                <div className="relative">
                                    <select
                                        value={months}
                                        onChange={(e) => {
                                            const newMonths = parseInt(e.target.value);
                                            setMonths(newMonths);
                                            // Ensure the 15-min floor when everything else is 0
                                            if (newMonths === 0 && parseInt(days) === 0 && parseInt(hours) === 0 && parseInt(minutes) === 0) {
                                                setMinutes(15);
                                            }
                                        }}
                                        className="w-full p-2 sm:p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] transition-all duration-300 hover:border-[#4ABDBD] bg-white appearance-none cursor-pointer text-gray-700 font-medium text-sm sm:text-base"
                                        data-testid="months-select"
                                    >
                                        {[...Array(13)].map((_, i) => (
                                            <option key={i} value={i} data-testid={`months-option-${i}`}>
                                                {i} {i === 1 ? t('doctorsettings.month_singular', 'month') : t('doctorsettings.months_plural', 'months')}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:pr-3 pointer-events-none">
                                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="block text-gray-600 font-semibold text-xs sm:text-sm uppercase tracking-wide" data-testid="days-label">
                                    {t('doctorsettings.days_label', 'Days')}
                                </label>
                                <div className="relative">
                                    <select
                                        value={days}
                                        onChange={(e) => {
                                            const newDays = parseInt(e.target.value);
                                            setDays(newDays);
                                            if (parseInt(months) === 0 && newDays === 0 && parseInt(hours) === 0 && parseInt(minutes) === 0) {
                                                setMinutes(15);
                                            }
                                        }}
                                        className="w-full p-2 sm:p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] transition-all duration-300 hover:border-[#4ABDBD] bg-white appearance-none cursor-pointer text-gray-700 font-medium text-sm sm:text-base"
                                        data-testid="days-select"
                                    >
                                        {[...Array(30)].map((_, i) => (
                                            <option key={i} value={i} data-testid={`days-option-${i}`}>
                                                {i} {i === 1 ? t('doctorsettings.day_singular', 'day') : t('doctorsettings.days_plural', 'days')}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:pr-3 pointer-events-none">
                                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="block text-gray-600 font-semibold text-xs sm:text-sm uppercase tracking-wide" data-testid="hours-label">
                                    {t('doctorsettings.hours_label')}
                                </label>
                                <div className="relative">
                                    <select
                                        value={hours}
                                        onChange={(e) => {
                                            const newHours = parseInt(e.target.value);
                                            setHours(newHours);
                                            if (parseInt(months) === 0 && parseInt(days) === 0 && newHours === 0 && parseInt(minutes) === 0) {
                                                setMinutes(15);
                                            }
                                        }}
                                        className="w-full p-2 sm:p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] transition-all duration-300 hover:border-[#4ABDBD] bg-white appearance-none cursor-pointer text-gray-700 font-medium text-sm sm:text-base"
                                        data-testid="hours-select"
                                    >
                                        {[...Array(24)].map((_, i) => (
                                            <option key={i} value={i} data-testid={`hours-option-${i}`}>
                                                {i} {i === 1 ? t('doctorsettings.hour') : t('doctorsettings.hours')}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:pr-3 pointer-events-none">
                                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="block text-gray-600 font-semibold text-xs sm:text-sm uppercase tracking-wide" data-testid="minutes-label">
                                    {t('doctorsettings.minutes_label')}
                                </label>
                                <div className="relative">
                                    <select
                                        value={minutes}
                                        onChange={(e) => setMinutes(e.target.value)}
                                        className="w-full p-2 sm:p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] transition-all duration-300 hover:border-[#4ABDBD] bg-white appearance-none cursor-pointer text-gray-700 font-medium text-sm sm:text-base"
                                        data-testid="minutes-select"
                                    >
                                        {(parseInt(months) > 0 || parseInt(days) > 0 || parseInt(hours) > 0) && (
                                            <option value={0} data-testid="minutes-option-0">{t('doctorsettings.minutes_00')}</option>
                                        )}
                                        <option value={15} data-testid="minutes-option-15">{t('doctorsettings.minutes_15')}</option>
                                        <option value={30} data-testid="minutes-option-30">{t('doctorsettings.minutes_30')}</option>
                                        <option value={45} data-testid="minutes-option-45">{t('doctorsettings.minutes_45')}</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:pr-3 pointer-events-none">
                                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Update Button */}
                <div className="flex justify-center pb-4">
                    <button
                        type="submit"
                        disabled={isSubmitting || loading}
                        className={`group flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 font-semibold text-sm sm:text-base min-w-[140px] sm:min-w-[160px] ${
                            isSubmitting || loading
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:scale-105 cursor-pointer hover:from-[#3AA8A8] hover:to-[#2D8A8A]'
                        }`}
                        data-testid="update-settings-button"
                    >
                        {isSubmitting ? (
                            <>
                                <svg
                                    className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                <span data-testid="updating-text">{t('doctorsettings.updating')}</span>
                            </>
                        ) : (
                            <span data-testid="update-text">{t('doctorsettings.update_settings')}</span>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default DoctorSettings;